/**
 * @fileoverview The words for a catalog app's price (APP-ENTITLEMENT-001 Phase 9), from a PUBLIC catalog row: its
 * published monthly unit prices per size dimension, what the anchor includes, a consumable's included quantity, a
 * share-of-recovery app's percentage, a free app's "free". Never a number typed here: every amount is a published
 * price row and every word a bundle leaf, formatted by the host's money formatter (`useFormatUsdFromCents`). The
 * ROW readers (`monthlyUnitPriceOf`, `sizeDimensionsOf`, `consumablesOf`, `isPriced`) are `@puffin-core/app-catalog`'s
 * (Phase 11): one way to read a public row for the Billing page, the signup basket and the marketing pricing section.
 *
 * @module @puffin-core/account-apps-ui/copy/priceCopy
 */

import {
  DIMENSION_KEY,
  PRICING_MODEL,
  consumablesOf,
  monthlyUnitPriceOf,
  sizeDimensionsOf,
} from '@puffin-core/app-catalog';

import { consumableKeyOf, unitKeyOf, unitsKeyOf } from './catalogCopy.js';

/**
 * The sentences a tile shows under an app's name: one per size dimension, then each inclusion.
 *
 * @param {Object} params
 * @param {Object} params.entry - a public catalog row.
 * @param {(key: string, params?: Object) => string} params.t - the bundle translator.
 * @param {(cents: number) => string} params.formatPrice - cents → money.
 * @returns {string[]}
 */
export function priceLinesOf({ entry, t, formatPrice }) {
  if (entry.pricingModel === PRICING_MODEL.INCLUDED_WITH_ANCHOR) return [t('free')];
  if (entry.pricingModel === PRICING_MODEL.SHARE_OF_RECOVERY) {
    return [t('shareOfRecovery', { percent: String(entry.shareBps / 100) })];
  }
  const lines = [];
  for (const dimension of sizeDimensionsOf(entry)) {
    const cents = monthlyUnitPriceOf(dimension);
    if (cents === null) {
      lines.push(t('priceUnpublished'));
    } else if (dimension.dimensionKey === DIMENSION_KEY.ACCOUNT) {
      lines.push(t('priceFlatMonthly', { price: formatPrice(cents) }));
    } else {
      lines.push(
        t('pricePerUnitMonthly', {
          price: formatPrice(cents),
          unit: t(unitKeyOf(dimension.dimensionKey)),
        }),
      );
    }
    if (dimension.parentDimensionKey !== null && dimension.includedQuantity > 0) {
      lines.push(
        t('includedWithParent', {
          count: String(dimension.includedQuantity),
          units: t(unitsKeyOf(dimension.dimensionKey)),
          parentUnit: t(unitKeyOf(dimension.parentDimensionKey)),
        }),
      );
    }
  }
  for (const consumable of consumablesOf(entry)) {
    const blockCents = monthlyUnitPriceOf(consumable);
    const units = t(consumableKeyOf(consumable.dimensionKey));
    // A published block price is said ({price} per {blockSize} {units}); an app priced by blocks alone says so when
    // none is published; nothing is said of a block price the console has not set on an app its sizes price.
    if (blockCents !== null) {
      lines.push(
        t('pricePerBlock', {
          price: formatPrice(blockCents),
          blockSize: String(consumable.blockSize),
          units,
        }),
      );
    } else if (sizeDimensionsOf(entry).length === 0) {
      lines.push(t('priceUnpublished'));
    }
    if (consumable.includedQuantity > 0) {
      lines.push(t('includedUnits', { count: String(consumable.includedQuantity), units }));
    }
  }
  return lines;
}
